import {AfterContentInit, Component, EventEmitter, OnDestroy, ViewChild} from '@angular/core';
import {Observable, Subscription}                                        from 'rxjs';
import {IApPermissionData, IGridData}                                    from '../../../ap-interface';
import {ApPermissions}                                                   from 'invoker-transport';
import {ApDynComponentComponent}                                         from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {ApDynGridColumnConfigBase}                                       from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {ApDynGridPagerConfigBase}                                        from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {ApDynGridPropertyColumnConfig}                                   from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridPagerWizardConfig,
  ApDynGridPagerWizardSection
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {MapViewStore}                                                    from '../../../stores/layout/mapview.store';
import {TranslationStore}                                                from '../../../stores/translation/translation.store';
import {filter, map}                                                     from 'rxjs/operators';
import {ApDynGridButtonColumnConfig}                                     from '../../../ap-dyngrids/config/ap-dyn-grid-button-column-config';
import {ParameterDefinitionStore}                                        from '../../../stores/parameter/parameter.definition.store';
import {FormStore}                                                       from '../../../stores/layout/form.store';
import {GetPermissionPipe}                                               from '../../../ap-permission/pipes/get-permission.pipe';
import {GetBooleanPipe}                                                  from '../../../ap-utils';
import {ApParameterDefinitionEditComponent}                              from '../../../entry-components/ap-administration-entry-components/ap-parameter-definition-edit.component';
import {GetParameterTypePipe}                                            from '../../../stores/pipes/ap-get-parameter-type.pipe';
import {GetParameterRefObjectTypePipe}                                   from '../../../stores/pipes/ap-get-parameter-ref-object-type.pipe';
import IParameterDefinition = Data.Parameter.IParameterDefinition;
import ParameterRefObjectType = Data.Parameter.Enums.ParameterRefObjectType;
import ParameterType = Data.Parameter.Enums.ParameterType;

interface IParameterDefinitionGridData extends IGridData<IParameterDefinition> {
}

@Component({
  selector: 'ap-translations',
  template: `
    <ap-dyncomponent [caption]="'Global_Parameter_Definition'"
                     [canSearch]="true"
                     [headerIcon]="'ap-icon-administration'"
                     [filterTooltip]="'filterTooltip'"
                     [columns]="columns"
                     [items]="items$ | async"
                     [loading$]="parameterDefinitionLoading$"
                     [pager]="pager"
                     [groupable]="true"
                     (create)="onCreate()"
                     [canCreate]="true"
                     [selectable]="{checkboxOnly: false, mode: 'multiple'}"
    ></ap-dyncomponent>`
})
export class ApParameterDefinitionComponent implements AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  columns: ApDynGridColumnConfigBase[] = [];
  items$: Observable<any>;
  pager: ApDynGridPagerConfigBase;
  parameterDefinitionLoading$: Observable<boolean>;
  public ApPermissions = ApPermissions;
  public apPermissionData: IApPermissionData;
  private _onEdit = new EventEmitter<any>();
  private _subscriptions: Array<Subscription> = [];

  constructor(private translationStore: TranslationStore,
              private mapViewStore: MapViewStore,
              private parameterDefinitionStore: ParameterDefinitionStore,
              private formStore: FormStore,
              private permissionPipe: GetPermissionPipe,
              private booleanPipe: GetBooleanPipe,
              private parameterTypePipe: GetParameterTypePipe,
              private parameterRefObjectTypePipe: GetParameterRefObjectTypePipe) {
    this.mapViewStore.hideMapView();
    this.parameterDefinitionStore.loadParameterDefinition();
    this.parameterDefinitionLoading$ = this.parameterDefinitionStore.Listen(s => s.loading);
    this.items$ = this.parameterDefinitionStore.Listen(s => s.data).pipe(
      filter(d => !!d),
      map(d => d.Convert<IParameterDefinitionGridData>(paraDef => {
        return {
          Id: paraDef.Id,
          SourceItem: paraDef,
          TypeName: this._parameterTypeConverter(paraDef.ParameterType),
          RefObjectTypeName: this._parameterRefObjectTypeConverter(paraDef.ParameterRefObjectType),
          Mandatory: this._boolean(paraDef.Mandatory),
          StandardIcon: 'pencilIcon',
          Unit: paraDef.Unit !== null ? paraDef.Unit : '-'
        };
      })));
    this._subscriptions.Add(this._onEdit.subscribe(event => {
      this.formStore.openForm({component: ApParameterDefinitionEditComponent, data: event.SourceItem});
    }));
  }

  ngAfterContentInit(): void {
    this.gridBuilder();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(d => {
      d.unsubscribe();
    });
  }

  onCreate(): void {
    this.formStore.openForm({component: ApParameterDefinitionEditComponent, data: null});
  }

  private gridBuilder(): void {
    this.permissionPipe.transform(ApPermissions.EDIT_TRANSLATIONS).subscribe(() => {
      this.columns = [
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.Name',
          title: 'Global__Name',
          headerFilterable: true,
          filterable: true,
          width: 50
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.TranslationKey',
          title: 'Global_Translation_Key',
          headerFilterable: true,
          filterable: true,
          width: 50
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.Description',
          title: 'Nutrients_Pages_Popups__Description',
          filterable: true,
          width: 50,
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'TypeName',
          title: 'Tasks_ViewModel__Type',
          filterable: true,
          width: 50,
          headerFilterable: true,
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'RefObjectTypeName',
          title: 'Global_Reference_Object',
          filterable: true,
          width: 50,
          headerFilterable: true,
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.StandardValue',
          title: 'Global_Default_Value',
          filterable: true,
          width: 50,
          headerFilterable: true,
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'Unit',
          title: 'Settings__Lbl_Menu_Unit',
          filterable: true,
          width: 50,
          headerFilterable: true,
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.Countries',
          title: 'Global_Countries',
          filterable: true,
          width: 50,
          headerFilterable: true,
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'Mandatory',
          title: 'Global_Mandatory',
          filterable: true,
          width: 50,
          headerFilterable: true,
          checkbox: true
        }),
        new ApDynGridButtonColumnConfig(
          {
            tooltip: 'Global__Edit',
            callback: this._onEdit,
            isStandardIcon: true,
            standardIcon: 'StandardIcon'
          }
        )
      ];
    });
    this.pager = new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardSection(),
      new ApDynGridPagerWizardSection(),
      new ApDynGridPagerWizardSection()
    );
  }

  onEdit(dataItem): void {
    this.formStore.openForm({component: ApParameterDefinitionEditComponent, data: dataItem.SourceItem});
  }

  private _parameterRefObjectTypeConverter(parameterRefObjectType: ParameterRefObjectType): string {
    return this.parameterRefObjectTypePipe.transform(parameterRefObjectType);
  }

  private _parameterTypeConverter(parameterType: ParameterType): string {
    return this.parameterTypePipe.transform(parameterType);
  }

  private _boolean(value: any): boolean {
    return this.booleanPipe.transform(value);
  }
}
